/**
 * TRENCH RUN — sixth cartridge, first scrolling cartridge (MVP).
 *
 * Heritage: Defender (1980) — horizontal scroll, radar at the top,
 * swooping enemy waves, civilian rescue — plus Choplifter (1982) for
 * the hovercraft VTOL + "land + wait + lift" rescue beat. Original
 * mechanic: **payload-weight handling**. Each civilian you carry
 * shaves your turn rate and top speed. 0 civs = nimble. 3 civs (the
 * cap) = sluggish drift. The risk/reward arc is the run back to base
 * — light and easy, or heavy and rich but vulnerable.
 *
 * Layout: 480×270 visible canvas, 1920px world. Camera follows the
 * player with a small lookahead bias toward the direction of travel.
 * Top 18px of canvas is the radar strip (full-trench scaled minimap).
 * Below it, the trench airspace; bottom 45px is the desert floor.
 *
 * Verbs: rescue (the cartridge's identity) + shoot (auxiliary). You
 * cannot shoot down — mines on the floor force you to descend into
 * their lob-arc range, and carrying 3 civs makes that descent slow.
 *
 * Determinism: all randomness routes through ctx.random (civilian
 * spawn x at level-start, enemy spawn timing + y-position + sine
 * phase, mine lob target jitter).
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  BASE_LIGHT,
  BASE_PAD,
  BASE_PAD_DIM,
  BULLET,
  BULLET_TRAIL,
  CIVILIAN_BODY,
  CIVILIAN_HEAD,
  CIVILIAN_WAVE,
  FLASH_HIT,
  FLOOR,
  FLOOR_DARK,
  FLOOR_LINE,
  HUD_ALERT,
  HUD_CARRY,
  HUD_LABEL,
  HUD_LEVEL,
  HUD_LIVES,
  HUD_SCORE,
  HUNTER_BODY,
  HUNTER_DARK,
  HUNTER_TRAIL,
  MINE_BARREL,
  MINE_BODY,
  MINE_DARK,
  MINE_SHOT,
  MINE_SHOT_TRAIL,
  PLAYER_BODY,
  PLAYER_COCKPIT,
  PLAYER_OUTLINE,
  PLAYER_THRUST,
  RADAR_BASE,
  RADAR_BG,
  RADAR_BORDER,
  RADAR_CIVILIAN,
  RADAR_ENEMY,
  RADAR_PLAYER,
  RIDGE_FAR,
  RIDGE_NEAR,
  SKY_HORIZON,
  SKY_MID,
  SKY_TOP,
  SWOOPER_BODY,
  SWOOPER_DARK,
  SWOOPER_EYE,
} from './palette.js';

// --- Canvas + world ---------------------------------------------------
const SCREEN_W = 480;
const SCREEN_H = 270;
const WORLD_W = 1920;
const RADAR_H = 18;
const FLOOR_TOP = 225; // y at which the floor surface begins
const FLOOR_H = SCREEN_H - FLOOR_TOP;
const AIR_TOP = RADAR_H + 4; // top boundary of player movement
const AIR_BOTTOM = FLOOR_TOP - 2; // bottom boundary (just above floor)

// Camera follows player with a small lookahead bias. CAMERA_X is the
// left edge of the visible window in world space.
const CAMERA_LOOKAHEAD = 60;

// --- Player -----------------------------------------------------------
const PLAYER_W = 18;
const PLAYER_H = 10;
const PLAYER_THRUST_ACCEL = 0.18;
const PLAYER_GRAVITY = 0.06;
const PLAYER_MAX_VX_BASE = 3.2;
const PLAYER_MAX_VY_BASE = 2.4;
/** Multiplier applied per carried civilian to max speed + acceleration.
 *  3 civs → 1 - 3*0.18 = 46% nimbleness. Tunable. */
const PAYLOAD_NIMBLE_PENALTY = 0.18;
const PLAYER_FIRE_COOLDOWN = 9;
const PLAYER_BULLET_VEL = 5.5;
const PLAYER_BULLET_LIFE = 90;
const PLAYER_BULLET_MAX = 8;

// --- Civilians --------------------------------------------------------
const CIVILIAN_W = 6;
const CIVILIAN_H = 10;
const PICKUP_DWELL_TICKS = 30; // 0.5s of hovering above the civilian
const PICKUP_X_TOLERANCE = 10; // px on either side of civilian x
const PICKUP_Y_TOLERANCE = 28; // descend within this of the floor
const CARRY_MAX = 3;
const PICKUP_SCORE = 50;
const DELIVERY_SCORE_PER_CIV = 200;
const LEVEL_CLEAR_BONUS_BASE = 500;

// --- Base -------------------------------------------------------------
const BASE_X_RIGHT = 80; // base zone: world x in [0, 80]

// --- Enemies ----------------------------------------------------------
type EnemyKind = 'swooper' | 'mine' | 'hunter';

// Swooper — sine-wave glider from edge.
const SWOOPER_W = 14;
const SWOOPER_H = 9;
const SWOOPER_SPEED = 1.4;
const SWOOPER_AMP = 28;
const SWOOPER_FREQ = 0.04;
const SWOOPER_HP = 1;
const SWOOPER_SCORE = 100;

// Mine — floor turret. Fixed positions, lobs arc shots.
const MINE_W = 14;
const MINE_H = 12;
const MINE_HP = 2;
const MINE_SCORE = 200;
const MINE_FIRE_INTERVAL = 150; // 2.5s between lobs
const MINE_LOB_VY = -3.6;
const MINE_LOB_GRAVITY = 0.07;

// Hunter — magenta homer, faster than the player at empty-carry.
const HUNTER_W = 12;
const HUNTER_H = 10;
const HUNTER_ACCEL = 0.08;
const HUNTER_MAX_SPEED = 3.6;
const HUNTER_HP = 1;
const HUNTER_SCORE = 300;

// Enemy bullets (mine shots).
const ENEMY_BULLET_LIFE = 220;
const ENEMY_BULLET_RADIUS = 3;

// --- Spawn cadence ----------------------------------------------------
// Cadence shortens with level.
const SWOOPER_BASE_INTERVAL = 220; // ticks
const HUNTER_BASE_INTERVAL = 360;
const SPAWN_INTERVAL_LEVEL_DROP = 18;
const SPAWN_INTERVAL_MIN = 80;

// --- Lives ------------------------------------------------------------
const LIVES_START = 3;
const RESPAWN_INVULN = 75; // 1.25s
const CRASH_FLASH_TICKS = 22;

type Phase = 'playing' | 'crashed' | 'level-clear' | 'game-over';

interface Vec {
  x: number;
  y: number;
}

interface Player extends Vec {
  vx: number;
  vy: number;
  /** Facing direction: -1 (left) / +1 (right). Tracks vx sign. */
  facing: -1 | 1;
  /** Number of civilians on board (0..CARRY_MAX). */
  carry: number;
  invuln: number;
  /** Active pickup target's index in the civilians array, or -1. */
  pickupTargetIdx: number;
  pickupDwell: number;
}

interface Civilian extends Vec {
  /** Wave-arm animation phase, in ticks. */
  wavePhase: number;
  /** True while the civilian is being lifted into the hovercraft. */
  rescued: boolean;
}

interface Bullet extends Vec {
  vx: number;
  vy: number;
  life: number;
  /** Source — player bullets damage enemies; enemy bullets damage the
   *  player. Mine lobs are 'enemy'; everything else is 'player'. */
  source: 'player' | 'enemy';
}

interface Enemy extends Vec {
  vx: number;
  vy: number;
  kind: EnemyKind;
  hp: number;
  /** Tick at spawn — used for sine-wave phase calc (Swooper). */
  born: number;
  /** Vertical anchor y for sine-wave motion (Swooper). */
  anchorY: number;
  /** Ticks until next fire (Mine). */
  fireTimer: number;
}

export class TrenchRunInstance implements GameInstance {
  private readonly ctx: GameContext;

  private tickCount = 0;
  private phase: Phase = 'playing';
  private crashFlash = 0;
  private levelClearTicks = 0;

  private score = 0;
  private lives = LIVES_START;
  private level = 1;
  private finalScore = 0;
  private finalLevel = 1;

  private player: Player;
  private civilians: Civilian[] = [];
  private enemies: Enemy[] = [];
  private bullets: Bullet[] = [];
  private cameraX = 0;
  private civiliansRemaining = 0; // not yet rescued / delivered

  private swooperSpawnTimer = 0;
  private hunterSpawnTimer = 0;

  private inputLeft = false;
  private inputRight = false;
  private inputUp = false;
  private inputDown = false;
  private inputFire = false;
  private restartArmed = false;

  // Sustained voices.
  private thrustLoop: LoopHandle | null = null;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.player = this.makePlayer();
    this.startLevel(1);
  }

  // -- GameInstance contract --------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inputLeft = state.left;
    this.inputRight = state.right;
    this.inputUp = state.up;
    this.inputDown = state.down;
    this.inputFire = state.fireA || state.start;
  }

  update(_dt: number): void {
    this.tickCount += 1;
    this.updateThrustAudio();

    if (this.phase === 'game-over') return;

    if (this.phase === 'crashed') {
      this.crashFlash -= 1;
      if (this.crashFlash <= 0) {
        if (this.lives > 0) {
          this.respawn();
        } else {
          this.finalScore = this.score;
          this.finalLevel = this.level;
          this.phase = 'game-over';
        }
      }
      this.advanceBullets();
      return;
    }

    if (this.phase === 'level-clear') {
      this.levelClearTicks -= 1;
      if (this.levelClearTicks <= 0) {
        this.startLevel(this.level + 1);
      }
      return;
    }

    if (this.player.invuln > 0) this.player.invuln -= 1;

    this.updatePlayer();
    this.advanceBullets();
    this.updateEnemies();
    this.updatePickup();
    this.updateDelivery();
    this.updateCamera();
    this.handleEnemySpawns();
    this.checkCollisions();
  }

  render(g: CanvasRenderingContext2D): void {
    // Sky gradient — sunset.
    const sky = g.createLinearGradient(0, RADAR_H, 0, FLOOR_TOP);
    sky.addColorStop(0, SKY_TOP);
    sky.addColorStop(0.55, SKY_MID);
    sky.addColorStop(1, SKY_HORIZON);
    g.fillStyle = sky;
    g.fillRect(0, RADAR_H, SCREEN_W, FLOOR_TOP - RADAR_H);

    // Distant ridgeline silhouette — parallax slower than world.
    this.renderRidges(g);

    // Trench floor.
    this.renderFloor(g);

    // World entities (translated by camera).
    this.renderCivilians(g);
    this.renderEnemies(g);
    this.renderBullets(g);
    this.renderBase(g);
    this.renderPlayer(g);

    // Radar strip on top.
    this.renderRadar(g);

    // Crash flash.
    if (this.phase === 'crashed' && this.crashFlash > CRASH_FLASH_TICKS - 5) {
      g.fillStyle = FLASH_HIT;
      g.fillRect(0, RADAR_H, SCREEN_W, SCREEN_H - RADAR_H);
    }

    this.renderHUD(g);

    if (this.phase === 'level-clear') this.renderLevelBanner(g);
    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(): void {
    // Fixed canvas.
  }
  pause(): void {
    // update() doesn't fire while paused — silence the drone manually.
    this.thrustLoop?.setGain(0);
  }
  resume(): void {
    // updateThrustAudio() restores gain on the next update tick.
  }
  teardown(): void {
    this.civilians = [];
    this.enemies = [];
    this.bullets = [];
    this.thrustLoop?.stop();
    this.thrustLoop = null;
  }

  getScore(): number {
    return Math.floor(this.phase === 'game-over' ? this.finalScore : this.score);
  }
  getFinalScore(): number {
    return Math.floor(this.phase === 'game-over' ? this.finalScore : this.score);
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // -- State transitions ------------------------------------------------

  private makePlayer(): Player {
    return {
      x: 60,
      y: AIR_TOP + 30,
      vx: 0,
      vy: 0,
      facing: 1,
      carry: 0,
      invuln: RESPAWN_INVULN,
      pickupTargetIdx: -1,
      pickupDwell: 0,
    };
  }

  private startLevel(level: number): void {
    this.level = level;
    this.player = this.makePlayer();
    this.enemies = [];
    this.bullets = [];
    this.cameraX = 0;

    // Civilians scatter across the trench, more per level.
    const count = 8 + (level - 1) * 2;
    this.civilians = [];
    for (let i = 0; i < count; i++) {
      // Spread civilians evenly with jitter, never inside the base zone.
      const slot = BASE_X_RIGHT + 80 + (i * (WORLD_W - BASE_X_RIGHT - 200)) / count;
      const jitter = (this.ctx.random.next() - 0.5) * 60;
      this.civilians.push({
        x: Math.max(BASE_X_RIGHT + 40, Math.min(WORLD_W - 40, slot + jitter)),
        y: FLOOR_TOP - CIVILIAN_H,
        wavePhase: Math.floor(this.ctx.random.next() * 60),
        rescued: false,
      });
    }
    this.civiliansRemaining = count;

    // Place mines at fixed positions per level — symmetric-ish spread.
    const mineCount = Math.min(4, 1 + level);
    for (let i = 0; i < mineCount; i++) {
      const mx =
        BASE_X_RIGHT + 200 + (i * (WORLD_W - BASE_X_RIGHT - 400)) / mineCount;
      this.enemies.push({
        x: mx,
        y: FLOOR_TOP - MINE_H,
        vx: 0,
        vy: 0,
        kind: 'mine',
        hp: MINE_HP,
        born: this.tickCount,
        anchorY: FLOOR_TOP - MINE_H,
        fireTimer: 60 + Math.floor(this.ctx.random.next() * MINE_FIRE_INTERVAL),
      });
    }

    this.swooperSpawnTimer = this.swooperInterval();
    this.hunterSpawnTimer = this.hunterInterval();
    this.phase = 'playing';
  }

  private respawn(): void {
    // Stand the player back up at base, drop any payload (already lost
    // on hit). Civilians and mines stay where they are; swoopers and
    // hunters are wiped for a clean restart of the air picture.
    this.player = this.makePlayer();
    this.enemies = this.enemies.filter((e) => e.kind === 'mine');
    this.bullets = [];
    this.swooperSpawnTimer = this.swooperInterval();
    this.hunterSpawnTimer = this.hunterInterval();
    this.phase = 'playing';
  }

  private swooperInterval(): number {
    return Math.max(
      SPAWN_INTERVAL_MIN,
      SWOOPER_BASE_INTERVAL - (this.level - 1) * SPAWN_INTERVAL_LEVEL_DROP
    );
  }

  private hunterInterval(): number {
    return Math.max(
      SPAWN_INTERVAL_MIN,
      HUNTER_BASE_INTERVAL - (this.level - 1) * SPAWN_INTERVAL_LEVEL_DROP
    );
  }

  // -- Player movement --------------------------------------------------

  private updatePlayer(): void {
    const p = this.player;
    const nimble = Math.max(0.25, 1 - PAYLOAD_NIMBLE_PENALTY * p.carry);
    const maxVx = PLAYER_MAX_VX_BASE * nimble;
    const maxVy = PLAYER_MAX_VY_BASE * nimble;
    const accel = PLAYER_THRUST_ACCEL * nimble;

    // Horizontal input.
    if (this.inputLeft && !this.inputRight) {
      p.vx -= accel;
      p.facing = -1;
    } else if (this.inputRight && !this.inputLeft) {
      p.vx += accel;
      p.facing = 1;
    } else {
      // Gentle horizontal damping when no input.
      p.vx *= 0.94;
    }

    // Vertical: gravity + optional thrust (Up = up, Down = descend).
    p.vy += PLAYER_GRAVITY;
    if (this.inputUp) p.vy -= accel * 1.4;
    else if (this.inputDown) p.vy += accel * 0.8;

    // Clamp velocity.
    if (p.vx > maxVx) p.vx = maxVx;
    if (p.vx < -maxVx) p.vx = -maxVx;
    if (p.vy > maxVy) p.vy = maxVy;
    if (p.vy < -maxVy) p.vy = -maxVy;

    p.x += p.vx;
    p.y += p.vy;

    // World clamps.
    if (p.x < 4) {
      p.x = 4;
      if (p.vx < 0) p.vx = 0;
    }
    if (p.x + PLAYER_W > WORLD_W - 4) {
      p.x = WORLD_W - 4 - PLAYER_W;
      if (p.vx > 0) p.vx = 0;
    }
    if (p.y < AIR_TOP) {
      p.y = AIR_TOP;
      if (p.vy < 0) p.vy = 0;
    }
    if (p.y + PLAYER_H > AIR_BOTTOM) {
      p.y = AIR_BOTTOM - PLAYER_H;
      if (p.vy > 0) p.vy = 0;
    }

    // Fire bullet if requested.
    if (this.inputFire && p.invuln <= 0) {
      this.fireBullet();
    }
  }

  private fireCooldown = 0;
  private fireBullet(): void {
    if (this.fireCooldown > 0) {
      this.fireCooldown -= 1;
      return;
    }
    const inFlight = this.bullets.filter(
      (b) => b.source === 'player'
    ).length;
    if (inFlight >= PLAYER_BULLET_MAX) {
      this.fireCooldown = 1;
      return;
    }
    const p = this.player;
    this.bullets.push({
      x: p.x + (p.facing === 1 ? PLAYER_W : 0),
      y: p.y + PLAYER_H / 2 - 1,
      vx: PLAYER_BULLET_VEL * p.facing,
      vy: 0,
      life: PLAYER_BULLET_LIFE,
      source: 'player',
    });
    this.fireCooldown = PLAYER_FIRE_COOLDOWN;
    this.ctx.audio.sfx('shoot');
  }

  // -- Camera -----------------------------------------------------------

  private updateCamera(): void {
    const p = this.player;
    const targetCamX =
      p.x - SCREEN_W / 2 + p.facing * CAMERA_LOOKAHEAD;
    // Lerp toward target for a slight follow lag.
    this.cameraX += (targetCamX - this.cameraX) * 0.18;
    // Clamp to world bounds.
    if (this.cameraX < 0) this.cameraX = 0;
    if (this.cameraX > WORLD_W - SCREEN_W) {
      this.cameraX = WORLD_W - SCREEN_W;
    }
    // Step the fire cooldown each tick (since fireBullet decrements
    // only when invoked).
    if (this.fireCooldown > 0) this.fireCooldown -= 1;
  }

  // -- Bullets ----------------------------------------------------------

  private advanceBullets(): void {
    for (const b of this.bullets) {
      b.x += b.vx;
      b.y += b.vy;
      if (b.source === 'enemy') {
        b.vy += MINE_LOB_GRAVITY;
      }
      b.life -= 1;
    }
    this.bullets = this.bullets.filter((b) => {
      if (b.life <= 0) return false;
      if (b.x < 0 || b.x > WORLD_W) return false;
      if (b.y < 0 || b.y > SCREEN_H) return false;
      return true;
    });
  }

  // -- Enemies ----------------------------------------------------------

  private updateEnemies(): void {
    for (const e of this.enemies) {
      if (e.kind === 'swooper') {
        // Glide horizontally, oscillate vertically.
        e.x += e.vx;
        const phase = (this.tickCount - e.born) * SWOOPER_FREQ;
        e.y = e.anchorY + Math.sin(phase) * SWOOPER_AMP;
      } else if (e.kind === 'mine') {
        // Stationary turret. Fire lob shots periodically when the
        // player is within shooting range.
        e.fireTimer -= 1;
        if (e.fireTimer <= 0) {
          this.lobMineShot(e);
          e.fireTimer = MINE_FIRE_INTERVAL;
        }
      } else if (e.kind === 'hunter') {
        // Accelerate toward player position (world space).
        const dx = this.player.x + PLAYER_W / 2 - (e.x + HUNTER_W / 2);
        const dy = this.player.y + PLAYER_H / 2 - (e.y + HUNTER_H / 2);
        const dist = Math.max(1, Math.hypot(dx, dy));
        e.vx += (dx / dist) * HUNTER_ACCEL;
        e.vy += (dy / dist) * HUNTER_ACCEL;
        const speed = Math.hypot(e.vx, e.vy);
        if (speed > HUNTER_MAX_SPEED) {
          e.vx = (e.vx / speed) * HUNTER_MAX_SPEED;
          e.vy = (e.vy / speed) * HUNTER_MAX_SPEED;
        }
        e.x += e.vx;
        e.y += e.vy;
      }
    }
    // Cull enemies that have wandered off the world.
    this.enemies = this.enemies.filter((e) => {
      if (e.x < -60 || e.x > WORLD_W + 60) return false;
      if (e.y < -40 || e.y > SCREEN_H + 40) return false;
      return true;
    });
  }

  private lobMineShot(e: Enemy): void {
    // Aim a slow arc shot at where the player is right now. Slight
    // PRNG-driven jitter so consecutive shots aren't a laser line.
    const jitter = (this.ctx.random.next() - 0.5) * 0.8;
    const dx = this.player.x + PLAYER_W / 2 - (e.x + MINE_W / 2) + jitter * 30;
    // Solve for vx using ballistic time-to-apex: t ≈ -2*vy / g.
    // (We don't solve for exact landing dy; the lob just needs to
    // travel the horizontal distance over the natural arc time.)
    const t = Math.max(20, (-2 * MINE_LOB_VY) / MINE_LOB_GRAVITY);
    const vx = dx / t;
    this.bullets.push({
      x: e.x + MINE_W / 2,
      y: e.y - 2,
      vx,
      vy: MINE_LOB_VY,
      life: ENEMY_BULLET_LIFE,
      source: 'enemy',
    });
    this.ctx.audio.sfx('mine-shot');
  }

  private handleEnemySpawns(): void {
    this.swooperSpawnTimer -= 1;
    this.hunterSpawnTimer -= 1;

    if (this.swooperSpawnTimer <= 0) {
      this.spawnSwooper();
      this.swooperSpawnTimer = this.swooperInterval();
    }
    if (this.hunterSpawnTimer <= 0) {
      this.spawnHunter();
      this.hunterSpawnTimer = this.hunterInterval();
    }
  }

  private spawnSwooper(): void {
    // Spawn from whichever screen edge is opposite the player's
    // facing — so the threat appears in front of them, not behind.
    const fromRight = this.player.facing === 1;
    const x = fromRight ? this.cameraX + SCREEN_W + 20 : this.cameraX - 20;
    const vx = fromRight ? -SWOOPER_SPEED : SWOOPER_SPEED;
    const anchorY =
      AIR_TOP + 30 + this.ctx.random.next() * (AIR_BOTTOM - AIR_TOP - 80);
    this.enemies.push({
      x,
      y: anchorY,
      vx,
      vy: 0,
      kind: 'swooper',
      hp: SWOOPER_HP,
      born: this.tickCount,
      anchorY,
      fireTimer: 0,
    });
    this.ctx.audio.sfx('enemy-spawn');
  }

  private spawnHunter(): void {
    const fromRight = this.ctx.random.next() < 0.5;
    const x = fromRight ? this.cameraX + SCREEN_W + 30 : this.cameraX - 30;
    const y = AIR_TOP + 10 + this.ctx.random.next() * (AIR_BOTTOM - AIR_TOP - 50);
    this.enemies.push({
      x,
      y,
      vx: fromRight ? -1 : 1,
      vy: 0,
      kind: 'hunter',
      hp: HUNTER_HP,
      born: this.tickCount,
      anchorY: y,
      fireTimer: 0,
    });
    this.ctx.audio.sfx('enemy-spawn');
  }

  // -- Rescue + delivery ------------------------------------------------

  private updatePickup(): void {
    const p = this.player;
    if (p.carry >= CARRY_MAX) {
      p.pickupTargetIdx = -1;
      p.pickupDwell = 0;
      return;
    }
    // Find a civilian we're hovering over.
    let target = -1;
    for (let i = 0; i < this.civilians.length; i++) {
      const c = this.civilians[i];
      if (!c || c.rescued) continue;
      const dx = Math.abs(p.x + PLAYER_W / 2 - (c.x + CIVILIAN_W / 2));
      const dy = c.y - (p.y + PLAYER_H);
      if (dx <= PICKUP_X_TOLERANCE && dy >= 0 && dy <= PICKUP_Y_TOLERANCE) {
        target = i;
        break;
      }
    }
    if (target !== p.pickupTargetIdx) {
      p.pickupTargetIdx = target;
      p.pickupDwell = 0;
    }
    if (target === -1) return;
    p.pickupDwell += 1;
    if (p.pickupDwell >= PICKUP_DWELL_TICKS) {
      const c = this.civilians[target];
      if (c) c.rescued = true;
      p.carry += 1;
      p.pickupTargetIdx = -1;
      p.pickupDwell = 0;
      this.civiliansRemaining -= 1;
      this.score += PICKUP_SCORE;
      this.ctx.audio.sfx('pickup');
    }
  }

  private updateDelivery(): void {
    const p = this.player;
    if (p.carry === 0) return;
    if (p.x > BASE_X_RIGHT) return;
    // Drop all carried civilians at base.
    this.score += DELIVERY_SCORE_PER_CIV * p.carry;
    this.ctx.audio.sfx('deliver');
    p.carry = 0;

    // Level clear when all delivered.
    if (this.civiliansRemaining === 0) {
      this.score += LEVEL_CLEAR_BONUS_BASE * this.level;
      this.phase = 'level-clear';
      this.levelClearTicks = 90;
      this.ctx.audio.sfx('level-up');
    }
  }

  // -- Collisions -------------------------------------------------------

  private checkCollisions(): void {
    const p = this.player;

    // Player bullets vs enemies.
    for (const b of this.bullets) {
      if (b.source !== 'player') continue;
      for (const e of this.enemies) {
        if (e.hp <= 0) continue;
        const ew = enemyW(e.kind);
        const eh = enemyH(e.kind);
        if (
          b.x >= e.x &&
          b.x <= e.x + ew &&
          b.y >= e.y &&
          b.y <= e.y + eh
        ) {
          e.hp -= 1;
          b.life = 0; // bullet consumed
          if (e.hp <= 0) {
            this.score += enemyScore(e.kind);
            this.ctx.audio.sfx('enemy-kill');
          }
          break;
        }
      }
    }
    // Cull dead enemies.
    this.enemies = this.enemies.filter((e) => e.hp > 0);

    // Enemy bullets vs player.
    if (p.invuln <= 0) {
      for (const b of this.bullets) {
        if (b.source !== 'enemy') continue;
        if (this.aabbHitCircle(p, b, ENEMY_BULLET_RADIUS)) {
          this.kill();
          return;
        }
      }
      // Enemy bodies vs player.
      for (const e of this.enemies) {
        const ew = enemyW(e.kind);
        const eh = enemyH(e.kind);
        if (this.aabbHit(p, e, ew, eh)) {
          this.kill();
          return;
        }
      }
    }
  }

  private aabbHit(a: Player, b: Enemy, bw: number, bh: number): boolean {
    return (
      a.x < b.x + bw &&
      a.x + PLAYER_W > b.x &&
      a.y < b.y + bh &&
      a.y + PLAYER_H > b.y
    );
  }

  private aabbHitCircle(a: Player, b: Bullet, r: number): boolean {
    const cx = a.x + PLAYER_W / 2;
    const cy = a.y + PLAYER_H / 2;
    const dx = cx - b.x;
    const dy = cy - b.y;
    return dx * dx + dy * dy < (PLAYER_W / 2 + r) * (PLAYER_W / 2 + r);
  }

  private kill(): void {
    this.player.carry = 0; // payload lost on hit
    this.lives -= 1;
    this.phase = 'crashed';
    this.crashFlash = CRASH_FLASH_TICKS;
    this.ctx.audio.sfx('crash');
  }

  // -- Audio (sustained voices) ----------------------------------------

  /**
   * Drive the thrust drone. Lazy-init the loop on first call, then
   * modulate gain to 0 (silent) when no thrust input or crashed /
   * game-over, and to THRUST_GAIN when held during normal play. The
   * loop stays alive across phase transitions to avoid click/pop.
   * Mirrors the Astrofall thrust pattern.
   */
  private updateThrustAudio(): void {
    if (!this.thrustLoop) {
      this.thrustLoop = this.ctx.audio.loop({
        wave: 'sawtooth',
        f: 95,
        gain: 0,
        fm: { ratio: 0.5, index: 15 },
      });
    }
    const thrusting =
      (this.inputUp || this.inputDown || this.inputLeft || this.inputRight) &&
      this.phase === 'playing';
    this.thrustLoop.setGain(thrusting ? 0.12 : 0);
  }

  // -- Rendering --------------------------------------------------------

  private renderRidges(g: CanvasRenderingContext2D): void {
    // Two ridgelines: far and near. Far moves slowly with camera (deep
    // parallax), near faster but still subordinate to the world. The
    // ridge tooth pattern uses a deterministic sin so it doesn't shift
    // between frames.
    const farOffset = this.cameraX * 0.1;
    const nearOffset = this.cameraX * 0.3;

    g.fillStyle = RIDGE_FAR;
    g.beginPath();
    g.moveTo(0, FLOOR_TOP);
    for (let x = 0; x <= SCREEN_W + 20; x += 8) {
      const wx = x + farOffset;
      const y = FLOOR_TOP - 14 - 6 * Math.sin(wx * 0.03);
      g.lineTo(x, y);
    }
    g.lineTo(SCREEN_W, FLOOR_TOP);
    g.closePath();
    g.fill();

    g.fillStyle = RIDGE_NEAR;
    g.beginPath();
    g.moveTo(0, FLOOR_TOP);
    for (let x = 0; x <= SCREEN_W + 20; x += 6) {
      const wx = x + nearOffset;
      const y = FLOOR_TOP - 6 - 4 * Math.sin(wx * 0.05 + 1.7);
      g.lineTo(x, y);
    }
    g.lineTo(SCREEN_W, FLOOR_TOP);
    g.closePath();
    g.fill();
  }

  private renderFloor(g: CanvasRenderingContext2D): void {
    g.fillStyle = FLOOR;
    g.fillRect(0, FLOOR_TOP, SCREEN_W, FLOOR_H);
    g.fillStyle = FLOOR_LINE;
    g.fillRect(0, FLOOR_TOP, SCREEN_W, 1);
    g.fillStyle = FLOOR_DARK;
    // Faint speed lines on the floor scroll with the camera so it
    // reads as moving.
    const dashOff = Math.floor(this.cameraX) % 30;
    for (let x = -dashOff; x < SCREEN_W; x += 30) {
      g.fillRect(x, FLOOR_TOP + 10, 14, 1);
    }
  }

  private renderBase(g: CanvasRenderingContext2D): void {
    // Base lives at world x in [0, BASE_X_RIGHT]; only render if visible.
    const screenLeft = -this.cameraX;
    const screenRight = BASE_X_RIGHT - this.cameraX;
    if (screenRight < 0 || screenLeft > SCREEN_W) return;
    g.fillStyle = BASE_PAD_DIM;
    g.fillRect(screenLeft, FLOOR_TOP - 6, BASE_X_RIGHT, 6);
    g.fillStyle = BASE_PAD;
    g.fillRect(screenLeft, FLOOR_TOP - 6, BASE_X_RIGHT, 1);
    // Beacon — blinking yellow light.
    if (Math.floor(this.tickCount / 24) % 2 === 0) {
      g.fillStyle = BASE_LIGHT;
      g.fillRect(screenLeft + BASE_X_RIGHT / 2 - 1, FLOOR_TOP - 14, 2, 8);
    }
  }

  private renderPlayer(g: CanvasRenderingContext2D): void {
    const p = this.player;
    if (p.invuln > 0 && p.invuln % 8 < 4) return;
    const sx = p.x - this.cameraX;
    const sy = p.y;
    // Body — flipped per facing.
    g.fillStyle = PLAYER_BODY;
    g.fillRect(sx, sy, PLAYER_W, PLAYER_H);
    g.fillStyle = PLAYER_OUTLINE;
    if (p.facing === 1) g.fillRect(sx + PLAYER_W - 2, sy, 2, PLAYER_H);
    else g.fillRect(sx, sy, 2, PLAYER_H);
    // Cockpit dot.
    g.fillStyle = PLAYER_COCKPIT;
    g.fillRect(sx + (p.facing === 1 ? PLAYER_W - 6 : 3), sy + 2, 3, 3);
    // Thrust flame when moving — points opposite of facing.
    if (this.inputLeft || this.inputRight || this.inputUp) {
      g.fillStyle = PLAYER_THRUST;
      const fx = p.facing === 1 ? sx - 4 : sx + PLAYER_W;
      g.fillRect(fx, sy + PLAYER_H / 2 - 1, 4, 2);
    }
    // Carry indicator — small orange dots stacked on top of the hovercraft.
    for (let i = 0; i < p.carry; i++) {
      g.fillStyle = CIVILIAN_BODY;
      g.fillRect(sx + 4 + i * 5, sy - 4, 3, 3);
    }
    // Pickup dwell meter — vertical bar above target civilian.
    if (p.pickupTargetIdx >= 0 && p.pickupDwell > 0) {
      const frac = p.pickupDwell / PICKUP_DWELL_TICKS;
      g.fillStyle = CIVILIAN_WAVE;
      g.fillRect(sx + PLAYER_W / 2 - 4, sy - 8, 8 * frac, 2);
      g.strokeStyle = HUD_LABEL;
      g.lineWidth = 1;
      g.strokeRect(sx + PLAYER_W / 2 - 4 + 0.5, sy - 8 + 0.5, 7, 1);
    }
  }

  private renderCivilians(g: CanvasRenderingContext2D): void {
    for (const c of this.civilians) {
      if (c.rescued) continue;
      const sx = c.x - this.cameraX;
      if (sx < -8 || sx > SCREEN_W + 8) continue;
      // Body.
      g.fillStyle = CIVILIAN_BODY;
      g.fillRect(sx, c.y + 3, CIVILIAN_W, CIVILIAN_H - 3);
      g.fillStyle = CIVILIAN_HEAD;
      g.fillRect(sx + 1, c.y, CIVILIAN_W - 2, 3);
      // Waving arm — alternates left/right every ~30 ticks.
      const wave = Math.floor((this.tickCount + c.wavePhase) / 30) % 2 === 0;
      g.fillStyle = CIVILIAN_WAVE;
      g.fillRect(wave ? sx - 2 : sx + CIVILIAN_W, c.y + 4, 2, 2);
    }
  }

  private renderEnemies(g: CanvasRenderingContext2D): void {
    for (const e of this.enemies) {
      const sx = e.x - this.cameraX;
      if (sx < -30 || sx > SCREEN_W + 30) continue;
      if (e.kind === 'swooper') {
        g.fillStyle = SWOOPER_BODY;
        g.fillRect(sx, e.y, SWOOPER_W, SWOOPER_H);
        g.fillStyle = SWOOPER_DARK;
        g.fillRect(sx, e.y + SWOOPER_H - 2, SWOOPER_W, 2);
        g.fillStyle = SWOOPER_EYE;
        g.fillRect(sx + (e.vx > 0 ? SWOOPER_W - 4 : 1), e.y + 2, 3, 3);
      } else if (e.kind === 'mine') {
        g.fillStyle = MINE_BODY;
        g.fillRect(sx, e.y, MINE_W, MINE_H);
        g.fillStyle = MINE_DARK;
        g.fillRect(sx, e.y + MINE_H - 3, MINE_W, 3);
        g.fillStyle = MINE_BARREL;
        g.fillRect(sx + MINE_W / 2 - 1, e.y - 2, 2, 4);
      } else if (e.kind === 'hunter') {
        // Hunter renders with a faint trail.
        g.fillStyle = HUNTER_TRAIL;
        g.fillRect(sx - e.vx * 2, e.y - e.vy * 2, HUNTER_W, HUNTER_H);
        g.fillStyle = HUNTER_BODY;
        g.fillRect(sx, e.y, HUNTER_W, HUNTER_H);
        g.fillStyle = HUNTER_DARK;
        g.fillRect(sx, e.y + HUNTER_H - 2, HUNTER_W, 2);
      }
    }
  }

  private renderBullets(g: CanvasRenderingContext2D): void {
    for (const b of this.bullets) {
      const sx = b.x - this.cameraX;
      if (sx < -8 || sx > SCREEN_W + 8) continue;
      if (b.source === 'player') {
        g.fillStyle = BULLET_TRAIL;
        g.fillRect(sx - Math.sign(b.vx) * 4, b.y, 4, 2);
        g.fillStyle = BULLET;
        g.fillRect(sx, b.y, 4, 2);
      } else {
        g.fillStyle = MINE_SHOT_TRAIL;
        g.fillRect(sx - 2, b.y - 2, 6, 6);
        g.fillStyle = MINE_SHOT;
        g.fillRect(sx - 1, b.y - 1, 3, 3);
      }
    }
  }

  private renderRadar(g: CanvasRenderingContext2D): void {
    g.fillStyle = RADAR_BG;
    g.fillRect(0, 0, SCREEN_W, RADAR_H);
    g.strokeStyle = RADAR_BORDER;
    g.lineWidth = 1;
    g.strokeRect(0.5, 0.5, SCREEN_W - 1, RADAR_H - 1);
    const scale = SCREEN_W / WORLD_W;
    // Base marker — leftmost.
    g.fillStyle = RADAR_BASE;
    g.fillRect(0, 2, Math.max(2, BASE_X_RIGHT * scale), RADAR_H - 4);
    // Civilians.
    for (const c of this.civilians) {
      if (c.rescued) continue;
      g.fillStyle = RADAR_CIVILIAN;
      g.fillRect(c.x * scale, RADAR_H - 5, 2, 3);
    }
    // Enemies.
    for (const e of this.enemies) {
      g.fillStyle = RADAR_ENEMY;
      g.fillRect(e.x * scale, 3, 2, 3);
    }
    // Player.
    g.fillStyle = RADAR_PLAYER;
    g.fillRect(this.player.x * scale, 8, 3, 4);
    // Viewport indicator — thin bracket showing what's on screen.
    g.strokeStyle = 'rgba(122, 243, 200, 0.4)';
    g.strokeRect(
      this.cameraX * scale + 0.5,
      1.5,
      SCREEN_W * scale - 1,
      RADAR_H - 3
    );
  }

  private renderHUD(g: CanvasRenderingContext2D): void {
    g.textAlign = 'left';
    g.textBaseline = 'top';
    // Bottom-left chrome: score + level.
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      String(Math.floor(this.score)).padStart(6, '0'),
      6,
      SCREEN_H - 16
    );
    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', 6, SCREEN_H - 26);

    // Center: carry indicator.
    g.textAlign = 'center';
    g.fillStyle = HUD_LABEL;
    g.fillText('CARRY', SCREEN_W / 2, SCREEN_H - 26);
    g.fillStyle = HUD_CARRY;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(
      `${this.player.carry} / ${CARRY_MAX}`,
      SCREEN_W / 2,
      SCREEN_H - 16
    );

    // Bottom-right: lives + level.
    g.textAlign = 'right';
    g.font = '8px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('LEVEL', SCREEN_W - 6, SCREEN_H - 26);
    g.fillStyle = HUD_LEVEL;
    g.font = 'bold 12px ui-monospace, monospace';
    g.fillText(String(this.level).padStart(2, '0'), SCREEN_W - 6, SCREEN_H - 16);

    // Lives — small hovercraft icons in the upper-right just under radar.
    g.fillStyle = HUD_LIVES;
    for (let i = 0; i < this.lives; i++) {
      g.fillRect(SCREEN_W - 10 - i * 8, RADAR_H + 4, 6, 3);
    }
  }

  private renderLevelBanner(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0, 0, 0, 0.55)';
    g.fillRect(0, RADAR_H, SCREEN_W, SCREEN_H - RADAR_H);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 18px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`LEVEL ${this.level} CLEAR`, SCREEN_W / 2, SCREEN_H / 2 - 10);
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText(
      `+${LEVEL_CLEAR_BONUS_BASE * this.level} CLEAR BONUS`,
      SCREEN_W / 2,
      SCREEN_H / 2 + 10
    );
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0, 0, 0, 0.72)';
    g.fillRect(0, 0, SCREEN_W, SCREEN_H);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = 'bold 22px ui-monospace, monospace';
    g.fillStyle = HUD_ALERT;
    g.fillText('GAME OVER', SCREEN_W / 2, SCREEN_H / 2 - 22);
    g.font = '11px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(
      `SCORE  ${String(Math.floor(this.finalScore)).padStart(6, '0')}`,
      SCREEN_W / 2,
      SCREEN_H / 2 + 2
    );
    g.fillStyle = HUD_LEVEL;
    g.fillText(`LEVEL  ${this.finalLevel}`, SCREEN_W / 2, SCREEN_H / 2 + 18);
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_LABEL;
    if (Math.floor(this.tickCount / 30) % 2 === 0) {
      g.fillText('PRESS Z TO RESTART', SCREEN_W / 2, SCREEN_H / 2 + 44);
    }
    g.textBaseline = 'top';
    g.textAlign = 'left';
  }
}

// --- Helpers ----------------------------------------------------------

function enemyW(kind: EnemyKind): number {
  if (kind === 'swooper') return SWOOPER_W;
  if (kind === 'mine') return MINE_W;
  return HUNTER_W;
}

function enemyH(kind: EnemyKind): number {
  if (kind === 'swooper') return SWOOPER_H;
  if (kind === 'mine') return MINE_H;
  return HUNTER_H;
}

function enemyScore(kind: EnemyKind): number {
  if (kind === 'swooper') return SWOOPER_SCORE;
  if (kind === 'mine') return MINE_SCORE;
  return HUNTER_SCORE;
}
